/**
 * <color-field> wires the three spectrum sliders to the rest of the page: the
 * gradients on the tracks, the hex beside them, and the big number on the
 * left. It creates nothing - without JavaScript the sliders still post their
 * hue, saturation and lightness, and the server mixes the color.
 */

import { hslToRgb, rgbToHex } from "../shared/color";

export class ColorField extends HTMLElement {
	#spectrum: HTMLElement | null = null;
	#output: HTMLOutputElement | null = null;

	connectedCallback(): void {
		this.#spectrum = this.querySelector(".spectrum");
		this.#output = this.querySelector("output");
		this.addEventListener("input", this.#onInput);
	}

	disconnectedCallback(): void {
		this.removeEventListener("input", this.#onInput);
	}

	/** The current slider positions, as the form would post them. */
	get mix(): { h: number; s: number; l: number } {
		const value = (name: string): number =>
			Number(this.querySelector<HTMLInputElement>(`input[name="${name}"]`)?.value ?? 0);
		return { h: value("h"), s: value("s"), l: value("l") };
	}

	set mix({ h, s, l }: { h: number; s: number; l: number }) {
		for (const [name, value] of [
			["h", h],
			["s", s],
			["l", l],
		] as const) {
			const input = this.querySelector<HTMLInputElement>(`input[name="${name}"]`);
			if (input) input.value = String(value);
		}
		this.paint();
	}

	#onInput = (): void => {
		this.removeAttribute("data-unset"); // they have chosen something now
		this.paint();
		const glyph = document.querySelector<HTMLElement>(".glyph");
		glyph?.removeAttribute("data-empty");
		glyph?.style.setProperty("--ink", this.hex);
	};

	get hex(): string {
		const { h, s, l } = this.mix;
		return rgbToHex(hslToRgb({ h, s: s / 100, l: l / 100 }));
	}

	/** Recolor the slider tracks and the readout from the current mix. */
	paint(): void {
		const { h, s, l } = this.mix;
		this.#spectrum?.style.setProperty("--h", String(h));
		this.#spectrum?.style.setProperty("--s", `${s}%`);
		this.#spectrum?.style.setProperty("--l", `${l}%`);
		if (this.#output) {
			this.#output.textContent = this.hex;
			this.#output.style.setProperty("--tint", this.hex);
		}
	}
}

if (!customElements.get("color-field")) customElements.define("color-field", ColorField);
